/**
 * SPILLESTEDER — salene andre hus har lagt ut, sett fra arrangøren, og stedsregisteret (agent B,
 * migrering 0410, `docs/SPILLESTED-KONSERT.md` skjerm 2 og 3).
 *
 * Alt om en sal i et annet hus kommer gjennom portene i 0410 med fast kolonneliste. Ingen form her
 * har et felt for leie, og kalenderen har ingen felt for hvem som holder en dato. Det eneste
 * «hvem» som finnes er `mine`: arrangørens EGNE forespørsler, lest i eget hus.
 */
import { z } from 'zod';

import { booleanQuerySchema } from './common.js';
import { venueDirectoryCardSchema, venuePlaceSchema } from './venue-directory.js';
import {
  venueHouseContactSchema,
  venueRoomContactSchema,
  venueVisibilitySchema,
} from './venues.js';

/** `past`: datoen har passert i salens tidssone (0432). Den vinner over alt annet. */
export const VENUE_DAY_STATES = ['free', 'hold', 'booked', 'closed', 'past'] as const;
export type VenueDayState = (typeof VENUE_DAY_STATES)[number];

/** Visningene på Spillesteder-siden. «Mine forespørsler» har sin egen rute. */
export const VENUE_LISTING_VIEWS = ['all', 'favorites', 'used'] as const;
export type VenueListingView = (typeof VENUE_LISTING_VIEWS)[number];

/** Arrangørens egne forespørsler som teller som aktive («Din forespørsel» i kalenderen). */
export const ACTIVE_DATE_REQUEST_STATUSES = ['requested', 'hold', 'confirmed'] as const;

const isoDate = z.iso.date();
const monthKey = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u);
const hhmm = z.string().regex(/^\d{2}:\d{2}$/u);

export const venueLayoutSchema = z
  .object({ name: z.string(), capacity: z.number().int() })
  .meta({ id: 'VenueLayout' });
export type VenueLayout = z.infer<typeof venueLayoutSchema>;

/** Arrangørens egen forespørsel på datoen, når det finnes en. */
export const venueDayMineSchema = z.object({
  requestId: z.uuid(),
  status: z.string(),
  holdRank: z.number().int().min(1).max(3).nullable(),
});

export const venueDaySchema = z
  .object({
    date: isoDate,
    state: z.enum(VENUE_DAY_STATES),
    /** Laveste ledige holdrang på en dato med hold; null når alle tre er tatt eller ingen hold. */
    nextHoldRank: z.number().int().min(1).max(3).nullable(),
    mine: venueDayMineSchema.nullable(),
  })
  .meta({ id: 'VenueDay' });
export type VenueDay = z.infer<typeof venueDaySchema>;

export const venueRoomCardSchema = z
  .object({
    roomId: z.uuid(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    venueName: z.string(),
    roomName: z.string(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    /** Fra Kartverket (1260). Fylle inn fra registerlaget når huset ikke har adressen selv. */
    county: z.string().nullable(),
    municipality: z.string().nullable(),
    capacityStanding: z.number().int().nullable(),
    capacitySeated: z.number().int().nullable(),
    stageWidthM: z.number().nullable(),
    stageDepthM: z.number().nullable(),
    nightlinerParking: z.boolean(),
    hasGrandPiano: z.boolean(),
    wheelchairSpaces: z.number().int().nullable(),
    layouts: z.array(venueLayoutSchema),
    defaultDoors: hhmm.nullable(),
    defaultCurfew: hhmm.nullable(),
    /** Første bilde på salen; hentes fra `/venue-market/rooms/:id/files/:fileId/content`. */
    imageFileId: z.uuid().nullable(),
    /** Registerets bilde når salen ikke har et eget (registerlaget, 1260). */
    imageUrl: z.string().nullable(),
    /** Funn 23: ledig/opptatt er publisert. Uten det har kortet ingen stripe for andre. */
    calendarPublished: z.boolean(),
    /** Funn 22/31: synligheten, bare på egne saler. */
    visibility: venueVisibilitySchema.nullable(),
    isFavorite: z.boolean(),
    /** Salen er vårt eget hus sin. */
    isOwn: z.boolean(),
    /** Vi har sendt en forespørsel eller laget en konsert mot salen før. */
    usedBefore: z.boolean(),
    /** Stripa: hver dag i den valgte måneden. */
    days: z.array(venueDaySchema),
  })
  .meta({ id: 'VenueRoomCard' });
export type VenueRoomCard = z.infer<typeof venueRoomCardSchema>;

export const venueListingResponseSchema = z
  .object({
    month: monthKey,
    rooms: z.array(venueRoomCardSchema),
    /** Salene i plattformregisteret som passer filtrene og ikke er koblet til en sal i Tvitvi. */
    directory: z.array(venueDirectoryCardSchema),
    /**
     * Fylkene og kommunene der det finnes saler vi får se — stedsfilteret (funn 24). Tellingen tar med
     * de andre filtrene (kapasitet, «må ha», ledig, søket), ikke stedet selv: et sted som ville gitt
     * null treff, står ikke (QA15-S I3, «ingen tomme valg»).
     */
    places: z.array(venuePlaceSchema),
    /** Hvor mange saler hvert «må ha» ville gitt med de andre filtrene. Null treff vises ikke. */
    mustHave: z
      .object({
        grandPiano: z.number().int().min(0),
        nightliner: z.number().int().min(0),
        wheelchair: z.number().int().min(0),
      })
      .meta({ id: 'VenueMustHaveCounts' }),
    /** Aktive forespørsler (sendt eller hold) i eget hus — tallet på «Mine forespørsler». */
    myRequestCount: z.number().int().min(0),
  })
  .meta({ id: 'VenueListingResponse' });
export type VenueListingResponse = z.infer<typeof venueListingResponseSchema>;

/** En gjentatt (`?cities=a&cities=b`) eller kommaseparert liste med korte tekster. */
const textListQuerySchema = z.preprocess(
  (value) => {
    if (value === undefined || value === null) return undefined;
    if (Array.isArray(value)) return value.map(String);
    const text = String(value).trim();
    return text.length === 0 ? undefined : text.split(',');
  },
  z.array(z.string().trim().min(1).max(120)).max(100),
);

const capacityQuery = z.coerce.number().int().min(0).max(1_000_000);

export const venueListingQuerySchema = z
  .object({
    view: z.enum(VENUE_LISTING_VIEWS).optional(),
    search: z.string().trim().max(120).optional(),
    /** Fylkesnavn. */
    counties: textListQuerySchema.optional(),
    /** «Fylke|Kommune» — kommunenavn er ikke unike i landet (Herøy). */
    municipalities: textListQuerySchema.optional(),
    minCapacity: capacityQuery.optional(),
    maxCapacity: capacityQuery.optional(),
    grandPiano: booleanQuerySchema.optional(),
    nightliner: booleanQuerySchema.optional(),
    wheelchair: booleanQuerySchema.optional(),
    /** Måneden stripa og «ledig i måneden» gjelder. Standard: inneværende måned. */
    month: monthKey.optional(),
    /** Bare saler som er ledige denne datoen. */
    freeOn: isoDate.optional(),
    /** Bare saler med minst én ledig dato i `month`. */
    freeInMonth: booleanQuerySchema.optional(),
  })
  .meta({ id: 'VenueListingQuery' });
export type VenueListingQuery = z.infer<typeof venueListingQuerySchema>;

export const venueRoomFileSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['image', 'document', 'link']),
  title: z.string(),
  url: z.string().nullable(),
  hasFile: z.boolean(),
  filename: z.string().nullable(),
  contentType: z.string().nullable(),
  byteSize: z.number().int().nullable(),
});
export type VenueRoomFile = z.infer<typeof venueRoomFileSchema>;

export const venueRoomFieldSchema = z.object({ label: z.string(), value: z.string() });

/**
 * SALENS SIDE UTENFRA. De faste feltene står som egne felt med samme navn som i huset
 * (`capacityStanding`, `stageWidthM` …), så `ROOM_SPEC_FIELDS` kan slå dem opp på nøkkel.
 */
export const venueRoomPublicSchema = z
  .object({
    roomId: z.uuid(),
    organizationId: z.uuid(),
    organizationName: z.string(),
    venueName: z.string(),
    roomName: z.string(),
    description: z.string().nullable(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    capacityStanding: z.number().int().nullable(),
    capacitySeated: z.number().int().nullable(),
    stageWidthM: z.number().nullable(),
    stageDepthM: z.number().nullable(),
    ceilingHeightM: z.number().nullable(),
    rigging: z.string().nullable(),
    pa: z.string().nullable(),
    monitor: z.string().nullable(),
    mixers: z.string().nullable(),
    lights: z.string().nullable(),
    backline: z.string().nullable(),
    power: z.string().nullable(),
    loading: z.string().nullable(),
    nightlinerParking: z.boolean(),
    parking: z.string().nullable(),
    dressingRooms: z.string().nullable(),
    bars: z.number().int().nullable(),
    accessibility: z.string().nullable(),
    wheelchairSpaces: z.number().int().nullable(),
    hasGrandPiano: z.boolean(),
    closedWeekdays: z.array(z.number().int().min(1).max(7)),
    defaultDoors: hhmm.nullable(),
    defaultCurfew: hhmm.nullable(),
    defaultGetIn: hhmm.nullable(),
    specVersion: z.number().int(),
    specChangedAt: z.iso.datetime({ offset: true }).nullable(),
    layouts: z.array(venueLayoutSchema),
    fields: z.array(venueRoomFieldSchema),
    files: z.array(venueRoomFileSchema),
    isFavorite: z.boolean(),
    isOwn: z.boolean(),
    /** Funn 22: synligheten på egen sal, så «Se som arrangør» kan si sannheten. Null for andres. */
    visibility: venueVisibilitySchema.nullable().optional(),
    calendarPublished: z.boolean().optional(),
    /**
     * 251.8: hvem du ringer om salen — salens kontaktperson og organisasjonens generelle kontakt.
     * API-et fyller begge (null når de mangler); valgfri så fiksturer uten feltene står.
     */
    contact: venueRoomContactSchema.nullable().optional(),
    houseContact: venueHouseContactSchema.nullable().optional(),
  })
  .meta({ id: 'VenueRoomPublic' });
export type VenueRoomPublic = z.infer<typeof venueRoomPublicSchema>;

export const venueAvailabilityQuerySchema = z
  .object({ month: monthKey.optional() })
  .meta({ id: 'VenueAvailabilityQuery' });
export type VenueAvailabilityQuery = z.infer<typeof venueAvailabilityQuerySchema>;

export const venueAvailabilityResponseSchema = z
  .object({ roomId: z.uuid(), month: monthKey, days: z.array(venueDaySchema) })
  .meta({ id: 'VenueAvailabilityResponse' });
export type VenueAvailabilityResponse = z.infer<typeof venueAvailabilityResponseSchema>;

export const venueFavoriteResponseSchema = z
  .object({ roomId: z.uuid(), isFavorite: z.boolean() })
  .meta({ id: 'VenueFavoriteResponse' });
export type VenueFavoriteResponse = z.infer<typeof venueFavoriteResponseSchema>;

/** Arrangørens egne datoforespørsler — «Mine forespørsler». Lest i eget hus. */
export const myDateRequestSchema = z.object({
  id: z.uuid(),
  concertId: z.uuid(),
  concertTitle: z.string().nullable(),
  requestedOn: isoDate,
  status: z.string(),
  holdRank: z.number().int().min(1).max(3).nullable(),
  holdUntil: isoDate.nullable(),
  roomId: z.uuid().nullable(),
  /** Salen i plattformregisteret (1260), før den er i Tvitvi. */
  directoryRoomId: z.uuid().nullable(),
  venueName: z.string().nullable(),
  roomName: z.string().nullable(),
  city: z.string().nullable(),
  artistName: z.string().nullable(),
  createdAt: z.iso.datetime({ offset: true }),
  /*
   * Bølge 3: det konsertens forespørselsliste (`GET /date-requests`, samme `date_request:list`)
   * allerede sender om den samme raden — det arrangøren ba om, og det huset svarte.
   */
  layoutName: z.string().nullable(),
  capacity: z.number().int().nullable(),
  doorsAt: hhmm.nullable(),
  curfewAt: hhmm.nullable(),
  expectedAudience: z.number().int().nullable(),
  message: z.string().nullable(),
  rentMinor: z.number().int().nullable(),
  rentCurrency: z.string().length(3),
  rentTerms: z.string().nullable(),
  merchPercent: z.number().nullable(),
  answerNote: z.string().nullable(),
  sentAt: z.iso.datetime({ offset: true }).nullable(),
  answeredAt: z.iso.datetime({ offset: true }).nullable(),
});
export type MyDateRequest = z.infer<typeof myDateRequestSchema>;

export const myDateRequestListResponseSchema = z
  .object({ items: z.array(myDateRequestSchema) })
  .meta({ id: 'MyDateRequestListResponse' });
export type MyDateRequestListResponse = z.infer<typeof myDateRequestListResponseSchema>;
